import styled, { css } from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { ANIMATION_FUNCTION, IconButton } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
const { carbon } = colorsPalettes;
export enum EFadePlacement {
    Top = "top",
    Bottom = "bottom",
    TopAndBottom = "topAndBottom",
}

const transitionDelay = (isCollapsed) => {
    return isCollapsed ? "300ms" : "0ms";
};

const basicTransition = `all 200ms ${ANIMATION_FUNCTION}`;

const getFadeCssStyles = (fadePlacement: EFadePlacement, componentHeight: number, isCollapsed: boolean) => {
    return `
        content: "";
        position: absolute;
        left: 0;
        height:10px
        width: ${isCollapsed ? "43px" : "231px"};
        background: linear-gradient(to ${fadePlacement}, rgba(255, 255, 255, 0) 0%, rgba(255,255,255,1) 100%);
        ${fadePlacement}: ${componentHeight}px;
        z-index: 1;
        transition: opacity 0.5s ${ANIMATION_FUNCTION};
        transition: ${basicTransition};
        transition-delay: ${transitionDelay(isCollapsed)};
        `;
};

const getCommonStyles = () => `display: flex;
    flex-direction: column;
    align-items: center;
    `;

export const COLLAPSED_PRIMARY_SIDEBAR_WIDTH = 48;
export const FULL_PRIMARY_SIDEBAR_WIDTH = 236;

export const PrimarySidebarWrapper = styled.div<{ isCollapsed: boolean }>`
    height: 100%;
    ${getCommonStyles()};
    align-items: flex-start;
    background-color: ${carbon[0]};
    transition: ${basicTransition};
    transition-delay: ${({ isCollapsed }) => transitionDelay(isCollapsed)};
    width: ${({ isCollapsed }) => `${isCollapsed ? COLLAPSED_PRIMARY_SIDEBAR_WIDTH : FULL_PRIMARY_SIDEBAR_WIDTH}px`};
    border-right: solid 1px ${colorsPalettes.carbon[50]};
    overflow: hidden; /* prevents early trigger for hover. DRI-5691 */
`;

export const ScrollableTopComponentsWrapper = styled.div<{
    isCollapsed: boolean;
    fadePlacement: EFadePlacement;
    bottomComponentHeight: number;
    headerComponentHeight: number;
}>`
    ${getCommonStyles()};
    align-items: flex-start;
    gap: 12px;
    overflow-y: auto;
    overflow-x: hidden;
    ${({ isCollapsed }) =>
        isCollapsed &&
        `-ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */`}
    &::-webkit-scrollbar {
        ${({ isCollapsed }) => isCollapsed && `display: none;`}
        width: 5px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${carbon[100]};
        border-radius: 5px;
        height: 50px;
    }
    ${({ fadePlacement, bottomComponentHeight, isCollapsed, headerComponentHeight }) => {
        const displayTopFade = fadePlacement === EFadePlacement.Top || fadePlacement === EFadePlacement.TopAndBottom;
        const displayBottomFade =
            fadePlacement === EFadePlacement.Bottom || fadePlacement === EFadePlacement.TopAndBottom;

        return css`
            ${displayTopFade &&
            `&:before {
                  ${getFadeCssStyles(EFadePlacement.Top, headerComponentHeight, isCollapsed)}
             }`}
            ${displayBottomFade &&
            `&:after {
                 ${getFadeCssStyles(EFadePlacement.Bottom, bottomComponentHeight, isCollapsed)}
             }`}
        `;
    }};

    transition: ${basicTransition};
    transition-delay: ${({ isCollapsed }) => transitionDelay(isCollapsed)};
    padding-left: ${({ isCollapsed }) => `${isCollapsed ? "6px" : "12px"}`};
    width: calc(100% - 6px);
`;

export const BottomComponentsWrapper = styled.div<{ isCollapsed: boolean; $hideMargin: boolean }>`
    ${getCommonStyles()};
    gap: 8px;
    margin-top: ${({ $hideMargin }) => ($hideMargin ? 0 : "auto")};

    transition: ${basicTransition};
    transition-delay: ${({ isCollapsed }) => transitionDelay(isCollapsed)};
    padding: ${({ isCollapsed }) => `${isCollapsed ? "8px 9px 12px 9px" : "8px 12px 12px 12px"}`};
`;

export const WarningsWrapper = styled.div`
    gap: 8px;
    margin-top: auto;
    margin-bottom: 8px;
`;

export const SecondaryItemsWrapper = styled.div`
    gap: 2px;
`;

export const SecondaryMiddleItemsWrapper = styled(SecondaryItemsWrapper)``;

export const HeaderWrapper = styled.div<{ isCollapsed: boolean }>`
    ${getCommonStyles()};
    padding: 12px 12px;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    width: 90%;
`;

export const SWLogoWrapper = styled(FlexRow)`
    position: relative;
    align-items: center;
`;

export const SolutionSwitcherIcon = styled(IconButton)`
    position: absolute;
    top: 50%;
    right: -5px;
    transform: translateY(-50%);

    & svg path {
        fill: ${colorsPalettes.carbon[500]} !important;
    }
`;

export const UserSettingsWrapper = styled.div`
    width: 100%;
`;

export const TopMenuComponentsWrapper = styled.div<{ isCollapsed }>`
    ${getCommonStyles()};
    gap: 2px;
    margin-left: ${({ isCollapsed }) => `${isCollapsed ? "3px" : "0"}`};
    transition: margin-left 200ms ${ANIMATION_FUNCTION};
    transition-delay: ${({ isCollapsed }) => transitionDelay(isCollapsed)};
`;

export const GroupMenuComponentsWrapper = styled.div<{ isCollapsed }>`
    ${getCommonStyles()};
    transition-delay: ${({ isCollapsed }) => transitionDelay(isCollapsed)};
    gap: 12px;
`;

export const BottomMenuComponentsWrapper = styled(TopMenuComponentsWrapper)<{ isCollapsed }>``;

export const SolutionSwitcherDropdownWrapper = styled.div<{ isCollapsed: boolean }>`
    transition: ${basicTransition};
    transition-delay: ${({ isCollapsed }) => transitionDelay(isCollapsed)};
    pointer-events: ${({ isCollapsed }) => `${isCollapsed ? "none" : "auto"}`};
    opacity: ${({ isCollapsed }) => `${isCollapsed ? 0 : 1}`};
`;

export const LogoPrimaryText = styled.span`
    color: ${colorsPalettes.carbon[500]};
    font-family: "DM Sans";
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 100%;
    letter-spacing: -0.28px;
`;

export const LogoSubText = styled(LogoPrimaryText)`
    font-size: 10px;
    font-weight: 300;
    line-height: 120%;
    letter-spacing: -0.2px;
`;

export const LogoTextWrapper = styled.div<{ $isCollapsed: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    opacity: ${({ $isCollapsed }) => ($isCollapsed ? 0 : 1)};
    transition: ${basicTransition};
    transition-delay: ${({ $isCollapsed }) => transitionDelay($isCollapsed)};
`;

export const SolutionLogoWrapper = styled(FlexRow)<{ $isCollapsed: boolean }>`
    align-items: center;
    width: 216px;
    gap: 5px;
    cursor: pointer;
    position: ${({ $isCollapsed }) => ($isCollapsed ? "relative" : "unset")};
    left: ${({ $isCollapsed }) => ($isCollapsed ? "-2px" : 0)};
    transition: left 200ms ${ANIMATION_FUNCTION};
    transition-delay: ${({ $isCollapsed }) => transitionDelay($isCollapsed)};
`;

export const LogoIconWrapper = styled.div`
    height: 32px;
    width: 32px;
`;

export const LinkTextOverflow = styled(Text)`
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    white-space: nowrap;
    max-width: 174px;
`;
